import { MesfModal } from "@dexteel/mesf-core";
import { Grid2, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { WeldTest } from "@/shared/models/WeldTest";
import {
	deleteWeldTest,
	saveWeldTest,
	slotPhotoFile,
	uploadWeldTestPhoto,
} from "@/shared/repositories/WeldTestRepository";
import { BendResult, BendTestResult } from "../../components/BendTestResult";
import { PhotoCapture } from "../../components/PhotoCapture";

export type WeldTestModalMode = "update" | "delete" | "";

type Props = {
	test: WeldTest | null;
	mode: WeldTestModalMode;
	onClose: () => void;
	onSaved: () => void;
	onError?: (e: Error) => void;
};

type Slot = { file: File | null; keepUrl: string | null };

export const EditWeldTestModal = ({
	test,
	mode,
	onClose,
	onSaved,
	onError,
}: Props) => {
	const [bend1, setBend1] = useState<BendResult | null>(null);
	const [bend2, setBend2] = useState<BendResult | null>(null);
	const [operator, setOperator] = useState("");
	const [notes, setNotes] = useState("");
	const [slot1, setSlot1] = useState<Slot>({ file: null, keepUrl: null });
	const [slot2, setSlot2] = useState<Slot>({ file: null, keepUrl: null });
	const [saving, setSaving] = useState(false);

	const isDelete = mode === "delete";
	const open = test != null && mode !== "";

	useEffect(() => {
		if (!test) return;
		setBend1(test.BendTest1Passed ? "OK" : "NOT_OK");
		setBend2(test.BendTest2Passed ? "OK" : "NOT_OK");
		setOperator(test.Operator ?? "");
		setNotes(test.Notes ?? "");
		setSlot1({ file: null, keepUrl: test.Photo1Path });
		setSlot2({ file: null, keepUrl: test.Photo2Path });
	}, [test]);

	const slotChange = (set: (s: Slot) => void) => (file: File | null) =>
		// A new capture replaces the saved photo; the X clears both.
		set({ file, keepUrl: null });

	const handleSubmit = async () => {
		if (!test) return;
		setSaving(true);
		try {
			if (isDelete) {
				await deleteWeldTest(test.TestId);
			} else {
				const path1 = slot1.file
					? await uploadWeldTestPhoto(slotPhotoFile(slot1.file, 1))
					: slot1.keepUrl;
				const path2 = slot2.file
					? await uploadWeldTestPhoto(slotPhotoFile(slot2.file, 2))
					: slot2.keepUrl;
				await saveWeldTest({
					testId: test.TestId,
					assetId: test.AssetId,
					productId: test.ProductId,
					bendTest1Passed: bend1 === "OK",
					bendTest2Passed: bend2 === "OK",
					photo1Path: path1,
					photo2Path: path2,
					notes: notes.trim() || null,
					operator: operator.trim() || null,
				});
			}
			onSaved();
			onClose();
		} catch (e) {
			onError?.(e as Error);
		} finally {
			setSaving(false);
		}
	};

	return (
		<MesfModal
			title={`${isDelete ? "DELETE" : "EDIT"} WELD TEST`}
			open={open}
			handleClose={onClose}
			maxWidth="md"
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					void handleSubmit();
				}}
			>
				<MesfModal.Content dividers style={{ padding: "20px 30px" }}>
					{isDelete && (
						<Typography color="error" sx={{ mb: 2 }}>
							Are you sure you want to delete this weld test?
						</Typography>
					)}
					<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
						{test?.PartNumber ?? "—"}
						{test?.ExtendedPartNumber ? ` · ${test.ExtendedPartNumber}` : ""} ·{" "}
						{test?.AssetName ?? "—"}
					</Typography>

					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<BendTestResult
								label="Bend Test 1"
								value={bend1}
								onChange={setBend1}
								disabled={isDelete}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<BendTestResult
								label="Bend Test 2"
								value={bend2}
								onChange={setBend2}
								disabled={isDelete}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<PhotoCapture
								label="Bend Test 1 Photo"
								file={slot1.file}
								existingUrl={slot1.keepUrl}
								onChange={slotChange(setSlot1)}
								disabled={isDelete}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<PhotoCapture
								label="Bend Test 2 Photo"
								file={slot2.file}
								existingUrl={slot2.keepUrl}
								onChange={slotChange(setSlot2)}
								disabled={isDelete}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<TextField
								label="Employee #"
								value={operator}
								onChange={(e) => setOperator(e.target.value)}
								fullWidth
								size="small"
								disabled={isDelete}
								slotProps={{ htmlInput: { maxLength: 50 } }}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							<TextField
								label="Notes"
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								multiline
								minRows={2}
								fullWidth
								size="small"
								disabled={isDelete}
								slotProps={{ htmlInput: { maxLength: 1000 } }}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "16px 30px" }}>
					<FooterModalButtons
						isSubmitLoading={saving}
						disabled={!isDelete && (bend1 == null || bend2 == null)}
						onHide={() => onClose()}
						buttonLabel={isDelete ? "Delete" : "Save"}
						buttonColor={isDelete ? "secondary" : "success"}
					/>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
